/**
 * LLM 模块入口（#89098）
 *
 * 数据面 `/.cloud/llm`：
 *   GET  /.cloud/llm/models
 *   POST /.cloud/llm/chat/completions
 *
 * 公开 API：
 *   cloud.llm.models.list()
 *   cloud.llm.chat.completions.create()
 *
 * 流式响应走 SSE（`text/event-stream`），分块解析由 `./llm/sse.ts` 处理。
 */

import { CLOUD_MODULE_PATHS } from '../paths.js';
import type { CloudRuntimeConfig } from '../types.js';
import { ChatCompletionsAPI } from './llm/chat.js';
import { ModelsAPI } from './llm/models.js';

export { getModelCapability, getModelDisplayName } from './llm/models.js';

export class LlmModule {
    /** 该模块的数据面基址。 */
    readonly baseUrl: string;

    /** Models namespace。 */
    readonly models: ModelsAPI;

    /** Chat namespace。 */
    readonly chat: {
        completions: ChatCompletionsAPI;
    };

    constructor(
        config: CloudRuntimeConfig,
        private readonly fetch: typeof globalThis.fetch,
    ) {
        this.baseUrl = `${config.endpoint}${CLOUD_MODULE_PATHS.llm}`;
        this.models = new ModelsAPI(this.baseUrl, this.fetch);
        this.chat = {
            completions: new ChatCompletionsAPI(this.baseUrl, this.fetch),
        };
    }
}

// Re-export public types for ergonomic consumption from the module entry.
export type {
    ChatCompletion,
    ChatCompletionChunk,
    ChatCompletionChoice,
    ChatCompletionChunkChoice,
    ChatCompletionCreateParams,
    ChatCompletionCreateParamsStreaming,
    CompletionUsage,
    ChatCompletionMessage,
    ChatCompletionRequestMessage,
    ChatCompletionAssistantMessage,
    ChatCompletionTool,
    ChatCompletionToolChoice,
    ChatCompletionResponseFormat,
    ChatCompletionContentPart,
    ChatCompletionRole,
    PublicLLMModel,
    PublicModelCapabilities,
    PublicModelModalities,
    PublicModelPricing,
    PublicModelReasoning,
} from './llm/types.js';
export { CloudOpenAIError } from './llm/errors.js';
export type { OpenAIErrorObject, CloudOpenAIErrorInit } from './llm/errors.js';
